import React, { useState, useEffect } from 'react';
import { legalService } from '../../services/api';

interface GenerateNoticeModalProps {
    isOpen: boolean;
    onClose: () => void;
    tenant: any;
}

const RRA_GROUNDS = [
    { id: '8', label: 'Ground 8: Serious Rent Arrears (Mandatory)', description: 'At least 2 months/8 weeks rent unpaid.' },
    { id: '10', label: 'Ground 10: Some Rent Arrears (Discretionary)', description: 'Some rent unpaid at time of notice.' },
    { id: '11', label: 'Ground 11: Persistent Delay in Rent (Discretionary)', description: 'History of late payments.' },
    { id: '1', label: 'Ground 1: Landlord Moving In (Mandatory)', description: 'Landlord or family member moving into property.' },
    { id: '1A', label: 'Ground 1A: Selling Property (Mandatory)', description: 'Landlord intends to sell the property.' }
];

export default function GenerateNoticeModal({ isOpen, onClose, tenant }: GenerateNoticeModalProps) {
    const [selectedGrounds, setSelectedGrounds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [generatedHtml, setGeneratedHtml] = useState<string | null>(null);
    const [noticeDateResult, setNoticeDateResult] = useState<any>(null);
    const [calculatingDate, setCalculatingDate] = useState(false);
    const [intendedServiceDate, setIntendedServiceDate] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedGrounds([]);
            setGeneratedHtml(null);
            setNoticeDateResult(null);
            setError(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !tenant || selectedGrounds.length === 0) {
            setNoticeDateResult(null);
            return;
        }

        setCalculatingDate(true);
        const rpd = tenant.rentPaymentDay;
        if (!rpd) {
            setCalculatingDate(false);
            return;
        }

        const groundCode = selectedGrounds[0];
        legalService.calculateNoticeDate({
            rentPaymentDay: rpd,
            tenancyStartDate: tenant.tenancyStartDate,
            groundType: groundCode,
            intendedServiceDate,
            freePeriodDays: tenant.freePeriodDays || 0,
            freePeriodGranted: tenant.freePeriodGranted || false,
        }).then((res: any) => {
            setNoticeDateResult(res.data?.data || res.data);
        }).catch(() => {
            setNoticeDateResult(null);
        }).finally(() => {
            setCalculatingDate(false);
        });
    }, [isOpen, tenant, selectedGrounds, intendedServiceDate]);

    if (!isOpen || !tenant) return null;

    const toggleGround = (id: string) => {
        setSelectedGrounds(prev =>
            prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
        );
    };

    const handleGenerate = async () => {
        if (selectedGrounds.length === 0) {
            setError('Please select at least one ground for possession.');
            return;
        }

        setLoading(true);
        setError(null);
        setGeneratedHtml(null);

        try {
            const response = await legalService.generateNotice({
                tenantId: tenant.id,
                grounds: selectedGrounds.map(id => RRA_GROUNDS.find(g => g.id === id)?.label || id),
                intendedServiceDate,
                rentPaymentDay: tenant.rentPaymentDay,
                tenancyStartDate: tenant.tenancyStartDate,
                freePeriodDays: tenant.freePeriodDays || 0,
                freePeriodGranted: tenant.freePeriodGranted || false,
            });
            setGeneratedHtml(response.data.html);
        } catch (err: any) {
            console.error('Generation failed:', err);
            setError(err.response?.data?.message || 'Failed to generate notice. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        if (!generatedHtml) return;
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(`
                <html>
                    <head>
                        <title>Possession Notice - ${tenant.name}</title>
                        <style>
                            body { font-family: 'Times New Roman', serif; padding: 40px; }
                            h3 { border-bottom: 1px solid #ccc; padding-bottom: 10px; }
                        </style>
                    </head>
                    <body>
                        ${generatedHtml}
                        <script>window.print();</script>
                    </body>
                </html>
            `);
            printWindow.document.close();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                                <i className="fas fa-gavel text-indigo-600"></i>
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    Generate Possession Notice
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        Generate a formal possession notice for <strong>{tenant.name}</strong>.
                                        This system uses RRA 2025 compliant templates (replacing Section 21).
                                    </p>

                                    {!generatedHtml ? (
                                        <div className="mt-4 text-left space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Grounds for Possession:</label>
                                                <div className="space-y-2 max-h-60 overflow-y-auto border rounded p-2">
                                                    {RRA_GROUNDS.map(ground => (
                                                        <div key={ground.id} className="flex items-start">
                                                            <div className="flex items-center h-5">
                                                                <input
                                                                    id={`ground-${ground.id}`}
                                                                    name={`ground-${ground.id}`}
                                                                    type="checkbox"
                                                                    checked={selectedGrounds.includes(ground.id)}
                                                                    onChange={() => toggleGround(ground.id)}
                                                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                                />
                                                            </div>
                                                            <div className="ml-3 text-sm">
                                                                <label htmlFor={`ground-${ground.id}`} className="font-medium text-gray-700">{ground.label}</label>
                                                                <p className="text-gray-500 text-xs">{ground.description}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {selectedGrounds.length > 0 && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Intended Service Date</label>
                                                    <input
                                                        type="date"
                                                        value={intendedServiceDate}
                                                        onChange={(e) => setIntendedServiceDate(e.target.value)}
                                                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                    />
                                                </div>
                                            )}

                                            {calculatingDate && (
                                                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                    <p className="text-sm text-gray-500">
                                                        <i className="fas fa-spinner fa-spin mr-2"></i>Calculating notice date...
                                                    </p>
                                                </div>
                                            )}

                                            {noticeDateResult && (
                                                <div className={`p-3 rounded-lg border ${
                                                    noticeDateResult.isValid
                                                        ? 'border-green-200 bg-green-50'
                                                        : 'border-red-200 bg-red-50'
                                                }`}>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className={`text-sm font-medium ${
                                                                noticeDateResult.isValid ? 'text-green-900' : 'text-red-900'
                                                            }`}>
                                                                {noticeDateResult.isValid ? 'Notice Date Valid' : 'Notice Date Invalid'}
                                                            </p>
                                                            <p className="text-xs mt-1 text-gray-600">
                                                                Earliest valid: <strong>{new Date(noticeDateResult.earliestValidDate).toLocaleDateString()}</strong>
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                Notice period: {noticeDateResult.noticePeriodLabel}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                <i className="fas fa-book mr-1"></i>{noticeDateResult.citation}
                                                            </p>
                                                        </div>
                                                        <i className={`fas ${noticeDateResult.isValid ? 'fa-check-circle text-green-600' : 'fa-exclamation-triangle text-red-600'} text-xl`}></i>
                                                    </div>
                                                    {noticeDateResult.warnings?.length > 0 && (
                                                        <div className="mt-2 space-y-1">
                                                            {noticeDateResult.warnings.map((w: any, i: number) => (
                                                                <p key={i} className="text-xs text-amber-700">
                                                                    <i className="fas fa-exclamation-circle mr-1"></i>{w.message}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {!noticeDateResult.isValid && (
                                                        <p className="text-xs text-red-600 mt-2">
                                                            Generating a notice with an invalid date may result in an unenforceable notice.
                                                            Please update the intended service date or select a different ground.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="mt-4 text-left">
                                            <div className="bg-gray-50 border border-gray-200 rounded p-4 h-64 overflow-y-auto text-sm font-serif whitespace-pre-wrap">
                                                <div dangerouslySetInnerHTML={{ __html: generatedHtml }} />
                                            </div>
                                        </div>
                                    )}

                                    {error && (
                                        <div className="mt-2 text-sm text-red-600">
                                            {error}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        {!generatedHtml ? (
                            <button
                                type="button"
                                onClick={handleGenerate}
                                disabled={loading || selectedGrounds.length === 0}
                                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                            >
                                {loading ? 'Generating...' : 'Generate Notice'}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handlePrint}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    <i className="fas fa-print mr-2"></i> Print / PDF
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setGeneratedHtml(null)}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Back to Edit
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
